import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider, Form, Modal, notification } from 'antd';

import { useDefaultFormErrorNotification } from '@/hooks';

import { PageProps } from '@/types/PageProps';

import { useCreateStockCorrection } from '@/core/domain/stock/StockCorrections/StockCorrections.hooks';

import { StockCorrectionForm } from '@/components/Movements/StockCorrections/StockCorrectionForm';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { SaveButton } from '@/components/_common/Buttons';
import {
  CreateStockCorrectionRequest,
  StockCorrectionFormValues,
} from '@/core/domain/stock/StockCorrections/StockCorrections.types';
import { useHistory } from 'react-router-dom';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';

const CreateStockCorrectionPage: React.FC<PageProps> = ({
  route,
}: PageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<StockCorrectionFormValues>();

  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  const mutation = useCreateStockCorrection();

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields();

      Modal.confirm({
        title: intl.formatMessage({
          id: 'stockCorrections.create.alert',
        }),
        content: intl.formatMessage({
          id: 'stockCorrections.create.alert.description',
        }),
        onOk: async () => {
          const obj: CreateStockCorrectionRequest = {
            ...values,
            items: values.items.map((item) => ({
              oldQuantity: item.oldQuantity,
              quantity: item.quantity,
              consumableId: item.consumableId,
              partId: item.partId,
            })),
          };
          const { result } = await mutation.mutateAsync(obj);

          notification.success({
            message: intl.formatMessage({
              id: 'stockCorrections.create.notification.success',
            }),
            description: intl.formatMessage({
              id: 'stockCorrections.create.notification.successDescription',
            }),
          });

          history.push(`/app/movements/stockCorrections/${result.id}`);
        },
      });
    } catch (err) {
      sendFormErrorNotification(err);
    }
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        title={route.name}
        extra={
          <SaveButton
            block
            type="primary"
            onClick={handleSaveClick}
            loading={mutation.isLoading}
          >
            <FormattedMessage id="commons.entity.save" />
          </SaveButton>
        }
      >
        <StockCorrectionForm form={form} />

        <Divider />

        <SpaceForButton>
          <ButtonBack path="/app/movements/stockCorrections" />
        </SpaceForButton>
      </TabContentWrapper>
    </>
  );
};

export default CreateStockCorrectionPage;
